import Link from "next/link";
import { useRouter } from "next/router";
import { MouseEvent, useState } from "react";
import ActivityCard from "@/components/ActivityCard";
import { HtmlRenderer } from "@/components/HtmlRenderer";
import MultiCarousel from "@/components/MultiCarousel";
import withLayout from "@/components/withLayout";
import BookmarkIcon from "@/components/icons/Bookmark";
import BookmarkCheckedIcon from "@/components/icons/BookmarkChecked";
import Checklist from "@/components/icons/Checklist";
import GoogleDrive from "@/components/icons/GoogleDrive";
import Materials from "@/components/icons/Materials";
import ShareLink from "@/components/icons/ShareLink";
import Stopwatch from "@/components/icons/Stopwatch";
import {
  ResourcePath,
  SavedResourcesAction,
  SavedResourcesToast,
} from "@/components/navigator/SavedResourcesToast";
import { ShareToast } from "@/components/navigator/ShareToast";
import { useAuth } from "@/contexts/auth/AuthContext";
import { useEntities } from "@/contexts/entities/EntitiesContext";
import { useSavedResources } from "@/contexts/savedResources/SavedResourcesContext";
import Analytics from "@/lib/analytics";
import { createShareUrl } from "@/lib/utils";
import { ErrorToast } from "@/components/navigator/ErrorToast";
import ChevronRight2 from "@/components/icons/ChevronRight2";
import {
  StyledButton,
  ButtonTypes,
  ButtonSizes,
  IconTypes,
} from "@/components/StyledButton";

type SavedResourcesToastState = {
  isOpen: boolean;
  currentAction: SavedResourcesAction | null;
};

const ActivityDetailPage = () => {
  const router = useRouter();
  const activitySlug = router.query.slug as string;

  const [hasLinkCopied, setHasLinkCopied] = useState(false);
  const [savedResourcesToastState, setSavedResourcesToastState] =
    useState<SavedResourcesToastState>({
      isOpen: false,
      currentAction: null,
    });

  const [isErrorToastOpen, setIsErrorToastOpen] = useState<boolean>(false);
  const { getEntityHierarchyFromActivitySlug, getActivitiesForOutcomeArea } =
    useEntities();
  const {
    activities: savedActivities,
    addActivity,
    removeActivity,
  } = useSavedResources();
  const { user, openLoginPrompt } = useAuth();

  const hierarchy = getEntityHierarchyFromActivitySlug(activitySlug);
  const { activity, outcomeArea } = hierarchy;

  if (!activity || !outcomeArea) {
    return <div>Activity Not Found</div>;
  }

  const relatedActivities = getActivitiesForOutcomeArea(outcomeArea.id).filter(
    (activity) => activity.urlSlug !== activitySlug,
  );

  const {
    name,
    slidesUrl,
    overview,
    context,
    actions,
    runTime,
    preparation,
    materials,
    competencyId,
    competencyName,
    subCompetencyId,
    subCompetencyName,
    learnerOutcomeId,
  } = activity;
  const embedUrl = `${activity?.embedUrl}?start=false&loop=false&delayms=3000`;

  const handleCopyLinkClick = (e: MouseEvent) => {
    e.preventDefault();
    Analytics.handleShareLinkClick(hierarchy);
    navigator.clipboard.writeText(createShareUrl());
    setHasLinkCopied(true);
  };

  const handleCopyToGDriveClick = (e: MouseEvent) => {
    if (!user) {
      e.preventDefault();
      openLoginPrompt("copy activity to google drive");
      return;
    }

    Analytics.handleActivitySlidesButtonsClick({
      buttonName: "Copy to my Google Drive",
      activityName: activity.name,
      activitySlug: activity.urlSlug,
      hierarchy,
    });
  };

  const handleCloseToast = () => {
    setSavedResourcesToastState((prev) => ({ ...prev, isOpen: false }));
  };

  const isActivitySaved = savedActivities.some(
    (savedActivity) => savedActivity.slug === activitySlug,
  );

  const handleSavedResourcesClick = async () => {
    if (!user) {
      openLoginPrompt("activity");
      return;
    }
    try {
      if (isActivitySaved) {
        await removeActivity(activitySlug);
        setSavedResourcesToastState({
          isOpen: true,
          currentAction: SavedResourcesAction.Remove,
        });
        Analytics.handleRemoveActivityFromSavedResourcesEvent(
          activitySlug,
          name,
        );
      } else {
        await addActivity(activitySlug);
        setSavedResourcesToastState({
          isOpen: true,
          currentAction: SavedResourcesAction.Add,
        });
        Analytics.handleAddActivityToSavedResourcesEvent(activitySlug, name);
      }
    } catch (e) {
      console.error(e);
      setIsErrorToastOpen(true);
    }
  };

  return (
    <div className="mx-4 mb-10 grid min-h-[calc(100vh-72px)] max-w-screen-large grid-cols-4 gap-x-4 text-xl small:grid-cols-8 medium:mx-6 medium:mb-[60px] medium:grid-cols-12 large:mx-auto large:gap-x-6 print:hidden">
      {/* Title */}
      <div className="col-span-full medium:col-span-10 medium:col-start-2 large:col-span-8 large:col-start-3">
        <div className="my-8 medium:my-10">
          <span className="text-base text-dark-color">Activity</span>
          <h1 className="mt-4 font-roobert text-[32px] medium:text-4xl">
            {name}
          </h1>
        </div>

        {/* iFrame */}
        <div>
          <iframe
            className="w-full"
            title="embedded activity"
            src={embedUrl}
            width="960"
            height="576"
            allowFullScreen={true}
          ></iframe>
        </div>
      </div>
      {/* Buttons */}
      <div className="col-span-full medium:col-span-7 medium:col-start-2 large:col-span-6 large:col-start-3">
        <div className="my-6 flex gap-2 medium:my-10 medium:gap-4">
          <StyledButton
            buttonType={ButtonTypes.Tertiary}
            size={ButtonSizes.Medium}
            iconType={IconTypes.Left}
            onClick={handleCopyToGDriveClick}
            aria-label="Copy to my Google Drive"
            className="medium:h-12"
            as="link"
            href={slidesUrl}
            target="_blank"
          >
            <GoogleDrive />
            <span className="text-base text-BG-60 ">
              Copy
              <span className="hidden small:inline"> to my Google Drive</span>
            </span>
          </StyledButton>

          <StyledButton
            buttonType={ButtonTypes.Tertiary}
            size={ButtonSizes.Medium}
            iconType={IconTypes.Only}
            onClick={handleCopyLinkClick}
            aria-label="Share"
            className="medium:h-12 medium:w-12 medium:p-3"
          >
            <ShareLink />
          </StyledButton>

          <StyledButton
            buttonType={ButtonTypes.Tertiary}
            size={ButtonSizes.Medium}
            iconType={IconTypes.Only}
            onClick={handleSavedResourcesClick}
            aria-label={
              isActivitySaved
                ? "Remove from saved resources"
                : "Add to saved resources"
            }
            className="medium:h-12 medium:w-12 medium:p-3"
          >
            {isActivitySaved ? <BookmarkCheckedIcon /> : <BookmarkIcon />}
          </StyledButton>
        </div>

        <div className=" col-span-full border-t border-tab-border medium:col-span-7 medium:col-start-2 large:col-span-6 large:col-start-3"></div>

        <div className="my-6 flex flex-row gap-4 align-middle medium:my-10">
          <div>
            <p className="text-sm leading-tight text-dark-color">Competency:</p>
            <Link
              href={`/?selection=${competencyId}`}
              shallow={true}
              aria-label="Competency"
              className={`flex underline hover:no-underline text-${learnerOutcomeId}-80 h-fit`}
            >
              <p className="text-base font-bold leading-tight">
                {competencyName}
              </p>
            </Link>
          </div>
          <ChevronRight2 className="my-auto shrink-0" />

          <div>
            <p className="text-sm leading-tight text-dark-color">
              Component Skill:
            </p>
            <Link
              href={`/?selection=${subCompetencyId}`}
              shallow={true}
              aria-label="Competency"
              className={`flex underline hover:no-underline text-${learnerOutcomeId}-80 h-fit`}
            >
              <p className="text-base font-bold leading-tight">
                {subCompetencyName}
              </p>
            </Link>
          </div>
        </div>

        <div className="col-span-full mb-6 border-t border-tab-border medium:col-span-7 medium:col-start-2 medium:mb-10 large:col-span-6 large:col-start-3"></div>

        {/* Panel X-Small-Small View*/}
        <div className="col-span-full medium:hidden">
          <div className="flex flex-col divide-y rounded-xl border bg-white small:flex-row small:divide-x small:divide-y-0">
            <div className="p-3 small:shrink small:grow small:basis-0">
              <div className="ml-3 mt-3 flex justify-start">
                <Stopwatch />
                <p className="ml-2 font-roobert text-lg">Run Time</p>
              </div>
              <span className="ml-3 mt-3 text-base text-neutral-600">
                {runTime}
              </span>
            </div>
            <div className="p-3 small:shrink small:grow small:basis-0">
              <div className="ml-3 mt-3 flex justify-start">
                <Materials />
                <p className="ml-2 font-roobert text-lg">Materials</p>
              </div>
              <p className="ml-3 mt-3 text-base text-neutral-600">
                {materials}
              </p>
            </div>
            <div className="p-3 small:shrink small:grow small:basis-0">
              <div className="ml-3 mt-3 flex justify-start">
                <Checklist />
                <p className="ml-2 font-roobert text-lg">Set up</p>
              </div>
              <p className="ml-3 mt-3 text-base text-neutral-600">
                {preparation}
              </p>
            </div>
          </div>
        </div>

        {/* Below iFrame Text*/}
        {overview && (
          <>
            <h2 className="mb-6 mt-8 font-roobert text-2xl font-medium text-dark-color medium:mb-8 medium:mt-10">
              What students learn
            </h2>
            <div className="text-base text-neutral-600">
              <HtmlRenderer html={overview} />
            </div>
          </>
        )}

        {actions && (
          <>
            <h2 className="mb-6 mt-8 font-roobert text-2xl font-medium text-dark-color medium:mb-8 medium:mt-10">
              What students do
            </h2>
            <div className="text-base text-neutral-600">
              <HtmlRenderer html={actions} />
            </div>
          </>
        )}

        {context && (
          <>
            <h2 className="mb-6 mt-8 font-roobert text-2xl font-medium text-dark-color medium:mb-8 medium:mt-10">
              Where it fits
            </h2>
            <div className="text-base text-neutral-600">
              <HtmlRenderer html={context} />
            </div>
          </>
        )}
      </div>

      {/* Panel Medium View*/}
      <div className="hidden pt-10 medium:col-span-3 medium:block large:col-span-2">
        <div className="flex  divide-y rounded-xl border bg-white text-base medium:flex-col">
          <div className="p-3 ">
            <div className="ml-3 mt-3 flex justify-start">
              <Stopwatch />
              <p className="ml-2 font-roobert text-lg">Run Time</p>
            </div>
            <span className="ml-3 mt-3 text-base text-neutral-600">
              {runTime}
            </span>
          </div>
          <div className="p-3 small:shrink small:grow small:basis-0">
            <div className="ml-3 mt-3 flex justify-start">
              <Materials />
              <p className="ml-2 font-roobert text-lg">Materials</p>
            </div>
            <p className="ml-3 mt-3 text-base text-neutral-600">{materials}</p>
          </div>
          <div className="p-3 small:shrink small:grow small:basis-0">
            <div className="ml-3 mt-3 flex justify-start">
              <Checklist />
              <p className="ml-2 font-roobert text-lg">Set up</p>
            </div>
            <p className="ml-3 mt-3 text-base text-neutral-600">
              {preparation}
            </p>
          </div>
        </div>
      </div>
      {relatedActivities?.length > 0 && (
        <>
          <div className="col-span-full my-8 border-t border-tab-border medium:col-span-10 medium:col-start-2 medium:my-10 large:col-span-8 large:col-start-3"></div>

          <div className="col-span-full medium:col-span-10 medium:col-start-2 large:col-span-8 large:col-start-3">
            <h2 className="mb-8 font-roobert text-2xl font-medium text-dark-color medium:mb-10">
              Related activities for {outcomeArea.name}
            </h2>
            <MultiCarousel
              options={{ loop: false }}
              slides={relatedActivities.map((activity, index) => {
                return (
                  <ActivityCard
                    key={index}
                    activity={activity}
                    index={index}
                    className="embla__slide col-span-4 medium:col-span-5 large:col-span-4"
                  />
                );
              })}
            />
          </div>
        </>
      )}
      <ShareToast
        isOpen={hasLinkCopied}
        handleClose={() => setHasLinkCopied((prevState) => !prevState)}
      />
      <SavedResourcesToast
        isOpen={savedResourcesToastState.isOpen}
        currentAction={savedResourcesToastState.currentAction}
        resourcePath={ResourcePath.Activity}
        handleClose={handleCloseToast}
      />
      <ErrorToast
        isOpen={isErrorToastOpen}
        handleClose={() => setIsErrorToastOpen(false)}
      />
    </div>
  );
};

export default withLayout(ActivityDetailPage);
